// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { apiClient } from '../services/api-client.js';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useLocale } from '../hooks/useLocale.js';
import { useInstalledLanguages } from '../hooks/useInstalledLanguages.js';
import { useAuth } from '../contexts/AuthContext.js';
import { usePermissionContext } from '../contexts/PermissionContext.js';
import { useModuleConfig } from '../contexts/ModuleConfigContext.js';
import { SIDEBAR_PERMISSION_MAP, SIDEBAR_MODULE_MAP } from '../config/route-permissions.js';
import { Permission } from '@vn-grc/shared';
import { SkipToContent } from '../components/SkipToContent.js';
import { NotificationBell } from '../components/NotificationBell.js';
import { UIAssistProvider } from '../modules/ui-assist/components/UIAssistProvider.js';
import { TourProvider, useTourContext } from '../contexts/TourContext.js';
import { TourRunner } from '../components/TourRunner.js';

interface MenuLeaf {
  label: string;
  to: string;
  requiredPermission?: string;
  testId?: string;
}

interface MenuSubGroup {
  label: string;
  children: MenuLeaf[];
}

type MenuChild = MenuLeaf | MenuSubGroup;

function isSubGroup(child: MenuChild): child is MenuSubGroup {
  return (child as MenuSubGroup).children !== undefined;
}

interface MenuItem {
  label: string;
  to?: string;
  icon?: string;
  requiredPermission?: string;
  moduleId?: string;
  children?: MenuChild[];
}

/**
 * Flatten a module's children into all leaf entries, whether nested under a
 * sub-group or at the top level. Used for active-route detection, permission
 * filtering, and `needsEnd` resolution for NavLinks.
 */
function flattenLeaves(children?: MenuChild[]): MenuLeaf[] {
  if (!children) return [];
  const leaves: MenuLeaf[] = [];
  for (const c of children) {
    if (isSubGroup(c)) leaves.push(...c.children);
    else leaves.push(c);
  }
  return leaves;
}

/** Sprint 25.3.4: L2 sidebar category groupings. */
const SIDEBAR_CATEGORIES = [
  {
    key: 'data_privacy_rights',
    modules: [
      '/admin/consent',
      '/admin/consent-properties',
      '/admin/classification',
      '/admin/data-subjects',
      '/admin/discovery',
      '/admin/data-mapping',
      '/admin/rights-requests',
    ],
  },
  { key: 'vendor_third_party', modules: ['/admin/governance', '/admin/vendor-risk'] },
  {
    key: 'risk_assessment',
    modules: ['/admin/assessments', '/admin/audit', '/admin/incidents', '/admin/privacy-scanner'],
  },
  {
    key: 'operations_automation',
    modules: [
      '/admin/forms',
      '/admin/connectors',
      '/admin/deletion-orchestration',
      '/admin/incident-ops',
      '/admin/localization',
      '/admin/monitoring',
      '/admin/program-governance',
      '/admin/cmp-integrations',
    ],
  },
  {
    key: 'task_workflow',
    modules: [
      '/admin/access-accountability',
      '/admin/organizations',
      '/admin/partner',
      '/admin/tasks',
    ],
  },
] as const;

const CATEGORY_STORAGE_KEY = 'grc_sidebar_categories';

function getItemPath(item: MenuItem): string {
  if (item.to) return item.to;
  const firstLeaf = flattenLeaves(item.children)[0];
  if (firstLeaf?.to) {
    const parts = firstLeaf.to.split('/');
    return '/' + parts.slice(1, 3).join('/');
  }
  return '';
}

function getCategoryForItem(item: MenuItem): string | null {
  const path = getItemPath(item);
  for (const cat of SIDEBAR_CATEGORIES) {
    if (cat.modules.some((m) => path.startsWith(m))) return cat.key;
  }
  return null;
}

function loadCategoryState(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(CATEGORY_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  // Default: all collapsed
  const defaults: Record<string, boolean> = {};
  for (const cat of SIDEBAR_CATEGORIES) defaults[cat.key] = false;
  return defaults;
}

function saveCategoryState(state: Record<string, boolean>): void {
  try {
    localStorage.setItem(CATEGORY_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

/** Simple inline SVG icons for sidebar menu items (AesirX style). */
const MenuIcon = ({ name }: { name: string }) => {
  const icons: Record<string, JSX.Element> = {
    dashboard: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="2" y="2" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
        <rect x="10" y="2" width="6" height="4" rx="1" stroke="currentColor" strokeWidth="1.5" />
        <rect x="2" y="10" width="6" height="4" rx="1" stroke="currentColor" strokeWidth="1.5" />
        <rect x="10" y="8" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
    rights: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path
          d="M9 2v14M5 6l4-4 4 4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    data: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <ellipse cx="9" cy="5" rx="6" ry="2.5" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M3 5v8c0 1.38 2.69 2.5 6 2.5s6-1.12 6-2.5V5"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path d="M3 9c0 1.38 2.69 2.5 6 2.5s6-1.12 6-2.5" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
    governance: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path
          d="M9 2L3 5v4c0 4 3 6.5 6 7.5 3-1 6-3.5 6-7.5V5L9 2z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path
          d="M7 9l2 2 3-4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    consent: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="3" y="3" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M6 9l2 2 4-4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    assessments: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path
          d="M4 14V8M9 14V4M14 14V10"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
    classification: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path
          d="M3 4h12M3 9h8M3 14h5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <circle cx="14" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
    monitoring: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M9 6v3l2 2"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    incidents: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M9 3l7 12H2L9 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M9 8v3M9 13v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    connectors: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path
          d="M4 7h3v4H4zM11 7h3v4h-3z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path d="M7 9h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M2 9H4M14 9h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    onboarding: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.5" />
        <path d="M9 6v4M9 12h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    audit: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path
          d="M5 3h8a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M6 7h6M6 10h6M6 13h3"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    people: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="6" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M1 15c0-2.8 2.2-5 5-5s5 2.2 5 5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <circle cx="13" cy="6" r="2" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M17 15c0-2.2-1.8-4-4-4-1 0-1.9.4-2.6 1"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    platform: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="6" r="3" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M3 16c0-3.3 2.7-6 6-6s6 2.7 6 6"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    settings: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M9 2v2M9 14v2M2 9h2M14 9h2M4.2 4.2l1.4 1.4M12.4 12.4l1.4 1.4M4.2 13.8l1.4-1.4M12.4 5.6l1.4-1.4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    deletion: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path
          d="M5 4V3a1 1 0 011-1h6a1 1 0 011 1v1M3 4h12M5 4v10a2 2 0 002 2h4a2 2 0 002-2V4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M8 7v5M10 7v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    discovery: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="8" cy="8" r="5" stroke="currentColor" strokeWidth="1.5" />
        <path d="M12 12l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M8 5.5v5M5.5 8h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    help: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M7 7a2 2 0 013.5 1.5c0 1-1.5 1.5-1.5 2.5M9 13.5h.01"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    globe: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M2 9h14M9 2c2.5 2.5 3 5 3 7s-.5 4.5-3 7M9 2c-2.5 2.5-3 5-3 7s.5 4.5 3 7"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </svg>
    ),
  };
  return icons[name] || null;
};

/**
 * Admin layout with collapsible sidebar navigation.
 * AesirX-style accordion menu with expandable sections.
 */
export function AdminLayout() {
  const { locale, setLocale, t } = useLocale();
  const { languages } = useInstalledLanguages();
  const { user, logout } = useAuth();
  const location = useLocation();
  const activeModule = useMemo(() => {
    if (location.pathname.startsWith('/admin/consent-properties')) return 'consent-properties';
    const moduleSegment = location.pathname.match(/^\/admin\/([^/]+)/)?.[1];
    return moduleSegment ?? 'dashboard';
  }, [location.pathname]);
  const { hasPermission } = usePermissionContext();
  const { isModuleEnabled } = useModuleConfig();
  const isProduction = import.meta.env.PROD || import.meta.env.MODE === 'production';

  // Sprint 25.3.4.1: Mobile sidebar state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const sidebarRef = useRef<HTMLElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close on Escape key and manage focus trap
  useEffect(() => {
    if (!mobileMenuOpen) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
        hamburgerRef.current?.focus();
        return;
      }
      // Focus trap: Tab cycles within sidebar
      if (e.key === 'Tab' && sidebarRef.current) {
        const focusable = sidebarRef.current.querySelectorAll<HTMLElement>(
          'a[href], button, select, input, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    // Focus first item in sidebar on open
    requestAnimationFrame(() => {
      const firstLink = sidebarRef.current?.querySelector<HTMLElement>('a, button');
      firstLink?.focus();
    });

    // Auto-scroll active module into view
    requestAnimationFrame(() => {
      const active = sidebarRef.current?.querySelector<HTMLElement>('.active');
      active?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileMenuOpen]);

  const menuItems: MenuItem[] = [
    // Dashboard always first
    { label: t.admin.nav.dashboard, to: '/admin', icon: 'dashboard' },
    // Alphabetical order (A–Z) for all modules
    {
      label: t.admin.nav.access_accountability,
      icon: 'audit',
      requiredPermission: SIDEBAR_PERMISSION_MAP.access_accountability,
      moduleId: SIDEBAR_MODULE_MAP.access_accountability,
      children: [
        {
          label: t.admin.sidebar_groups.access_live_monitoring,
          children: [
            {
              label: t.access_accountability.nav.session_dashboard,
              to: '/admin/access-accountability/sessions',
            },
            {
              label: t.access_accountability.nav.access_log,
              to: '/admin/access-accountability/access-log',
              testId: 'access-log-tab',
            },
            {
              label: t.access_accountability.nav.anchoring_status,
              to: '/admin/access-accountability/anchoring',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.access_reviews_investigations,
          children: [
            {
              label: t.access_accountability.nav.review_campaigns,
              to: '/admin/access-accountability/review-campaigns',
            },
            {
              label: t.access_accountability.nav.justification_review,
              to: '/admin/access-accountability/justifications',
              testId: 'justification-tab',
            },
            {
              label: t.access_accountability.nav.pattern_analysis,
              to: '/admin/access-accountability/patterns',
            },
            {
              label: t.access_accountability.nav.investigations,
              to: '/admin/access-accountability/investigations',
              testId: 'investigations-tab',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.access_evidence_policies,
          children: [
            {
              label: t.access_accountability.nav.evidence_packages,
              to: '/admin/access-accountability/evidence-packages',
            },
            {
              label: t.access_accountability.nav.access_policies,
              to: '/admin/access-accountability/access-policies',
            },
          ],
        },
        {
          label: t.access_accountability.nav.configuration,
          to: '/admin/access-accountability/config',
          testId: 'config-tab',
        },
      ],
    },
    {
      label: t.admin.nav.assessments,
      icon: 'assessments',
      requiredPermission: SIDEBAR_PERMISSION_MAP.assessments,
      moduleId: SIDEBAR_MODULE_MAP.assessments,
      children: [
        { label: t.assessments.nav.dashboard, to: '/admin/assessments/dashboard' },
        { label: t.assessments.nav.dpias, to: '/admin/assessments/dpias' },
        {
          label: t.assessments?.dossier_filings?.nav_label ?? 'Dossier Filings',
          to: '/admin/assessments/dossier-filings',
        },
        // Sprint 44.4.0 — operator-facing materialisation health
        {
          label:
            (t.assessments as any)?.materialisation_health?.nav_label ??
            'Submission artifact health',
          to: '/admin/assessments/dossier-materialisation-health',
        },
        { label: t.assessments.nav.gap_report, to: '/admin/assessments/gap-report' },
        { label: t.assessments.nav.transfers, to: '/admin/assessments/transfers' },
      ],
    },
    {
      label: t.admin.nav.audit_trail,
      icon: 'audit',
      requiredPermission: SIDEBAR_PERMISSION_MAP.audit_trail,
      moduleId: SIDEBAR_MODULE_MAP.audit_trail,
      children: [
        { label: t.admin.nav.audit_trail, to: '/admin/audit' },
        {
          label:
            (t.admin.nav as unknown as Record<string, string>).unified_audit || 'Unified Audit',
          to: '/admin/audit/unified',
        },
        {
          label:
            (t.admin.nav as unknown as Record<string, string>).evidence_packs || 'Evidence Packs',
          to: '/admin/audit/evidence-packs',
        },
        {
          label:
            (t.admin.nav as unknown as Record<string, string>).audit_periods || 'Audit Periods',
          to: '/admin/audit/periods',
        },
      ],
    },
    {
      label: t.admin.nav.classification,
      icon: 'classification',
      requiredPermission: SIDEBAR_PERMISSION_MAP.classification,
      moduleId: SIDEBAR_MODULE_MAP.classification,
      children: [
        {
          label: t.admin.sidebar_groups.classification_taxonomies,
          children: [
            { label: t.classification.nav.taxonomies, to: '/admin/classification/taxonomies' },
            { label: t.classification.nav.labeling, to: '/admin/classification/labeling' },
            { label: t.classification.nav.lifecycle, to: '/admin/classification/lifecycle' },
          ],
        },
        {
          label: t.admin.sidebar_groups.classification_workflow,
          children: [
            {
              label: t.classification.review_tasks.title,
              to: '/admin/classification/review-tasks',
            },
            { label: t.classification.exceptions.title, to: '/admin/classification/exceptions' },
            { label: t.classification.nav.gaps, to: '/admin/classification/gaps' },
          ],
        },
        {
          label: t.admin.sidebar_groups.classification_insights,
          children: [
            {
              label: t.classification.enterprise_map.title,
              to: '/admin/classification/enterprise-map',
            },
            {
              label: t.classification.discovery.title,
              to: '/admin/classification/discovery-cycles',
            },
          ],
        },
        { label: t.classification.nav.exports, to: '/admin/classification/exports' },
      ],
    },
    {
      label: t.admin.nav.compliance_forms,
      icon: 'audit',
      requiredPermission: SIDEBAR_PERMISSION_MAP.compliance_forms,
      moduleId: SIDEBAR_MODULE_MAP.compliance_forms,
      children: [
        { label: t.admin.nav.forms_dashboard, to: '/admin/forms' },
        { label: t.admin.nav.form_catalog, to: '/admin/forms/catalog' },
        { label: t.admin.nav.form_templates, to: '/admin/forms/templates' },
        {
          label: (t.forms as any).nav?.filing_case_links || 'Filing Case Links',
          to: '/admin/forms/filing-case-links',
        },
        {
          label: (t.forms as any).nav?.section_routing || 'Section Routing',
          to: '/admin/forms/section-routing',
        },
        {
          label: (t.forms as any).nav?.submission_packages || 'Submission Packages',
          to: '/admin/forms/submission-packages',
        },
      ],
    },
    {
      label: t.admin.nav.connectors,
      icon: 'connectors',
      requiredPermission: SIDEBAR_PERMISSION_MAP.connectors,
      moduleId: SIDEBAR_MODULE_MAP.connectors,
      children: [
        {
          label: t.admin.sidebar_groups.connectors_runtime,
          children: [
            { label: t.connectors.nav.instances, to: '/admin/connectors/instances' },
            { label: t.connectors.nav.credentials, to: '/admin/connectors/credentials' },
            { label: t.connectors.nav.event_bus, to: '/admin/connectors/event-bus' },
            { label: t.connectors.nav.definitions, to: '/admin/connectors/definitions' },
          ],
        },
        {
          label: t.admin.sidebar_groups.connectors_due_diligence,
          children: [
            {
              label: t.connectors.nav.security_reviews,
              to: '/admin/connectors/security-reviews',
            },
            { label: t.connectors.nav.poc_scorecards, to: '/admin/connectors/poc-scorecards' },
            { label: t.connectors.nav.brds, to: '/admin/connectors/brds' },
            {
              label: t.connectors.nav.automation_planning,
              to: '/admin/connectors/automation-planning',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.connectors_coverage,
          children: [
            { label: t.connectors.nav.system_inventory, to: '/admin/connectors/system-inventory' },
            {
              label: t.connectors.nav.traceability_matrix,
              to: '/admin/connectors/traceability-matrix',
            },
            { label: t.connectors.nav.gap_register, to: '/admin/connectors/gap-register' },
          ],
        },
      ],
    },
    {
      label: t.admin.nav.consent,
      icon: 'consent',
      requiredPermission: SIDEBAR_PERMISSION_MAP.consent,
      moduleId: SIDEBAR_MODULE_MAP.consent,
      children: [
        {
          label: t.admin.sidebar_groups.consent_cmp_webhooks,
          children: [
            { label: t.admin.nav.cmp_integration, to: '/admin/consent/cmp-integration' },
            { label: t.admin.nav.bulk_sync, to: '/admin/consent/cmp-integration/remap' },
            {
              label: t.admin.nav.purpose_mapping,
              to: '/admin/consent/cmp-integration/purpose-mapping',
            },
            { label: t.admin.nav.webhook_config, to: '/admin/consent/cmp-integration/webhook' },
            { label: t.consent.nav.snapshots, to: '/admin/consent/snapshots' },
            { label: t.consent.sync.title, to: '/admin/consent/sync' },
            { label: t.consent.reconciliation.title, to: '/admin/consent/reconciliation' },
            { label: t.consent.nav.change_awareness, to: '/admin/consent/change-awareness' },
          ],
        },
        { label: t.consent.nav.audit, to: '/admin/consent/audit' },
        { label: t.consent.nav.policies, to: '/admin/consent/policies' },
        { label: t.consent.nav.purposes, to: '/admin/consent/purposes' },
        // Sprint 48.1.4 — Forseti consent gap analysis
        {
          label: t.consent?.gap_analysis?.nav_label ?? 'Gap Analysis',
          to: '/admin/consent/gap-analysis',
        },
        { label: t.consent.nav.workflows, to: '/admin/consent/workflows' },
        {
          label: t.consent?.import?.nav_title ?? 'Bulk Import',
          to: '/admin/consent/workflows/import',
        },
        { label: t.consent.propagation.title, to: '/admin/consent/withdrawal-propagation' },
      ],
    },
    {
      label: t.admin.nav.consent_properties,
      icon: 'consent',
      requiredPermission: SIDEBAR_PERMISSION_MAP.consent_properties,
      moduleId: SIDEBAR_MODULE_MAP.consent_properties,
      children: [
        { label: t.consent_properties.nav.dashboard, to: '/admin/consent-properties/dashboard' },
        { label: t.consent_properties.analytics.title, to: '/admin/consent-properties/analytics' },
        {
          label: t.admin.sidebar_groups.cp_definitions,
          children: [
            {
              label: t.consent_properties.nav.properties,
              to: '/admin/consent-properties/properties',
              testId: 'properties-tab',
            },
            {
              label: t.consent_properties.nav.groups,
              to: '/admin/consent-properties/groups',
              testId: 'groups-tab',
            },
            {
              label: t.consent_properties.nav.golden_templates,
              to: '/admin/consent-properties/golden-templates',
              testId: 'golden-templates-tab',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.cp_lifecycle,
          children: [
            {
              label: t.consent_properties.nav.bindings,
              to: '/admin/consent-properties/bindings',
              testId: 'bindings-tab',
            },
            {
              label: t.consent_properties.nav.change_requests,
              to: '/admin/consent-properties/change-requests',
            },
            {
              label: t.consent_properties.rollouts.title,
              to: '/admin/consent-properties/rollouts',
            },
            {
              label: t.consent_properties.nav.snapshot_schedules,
              to: '/admin/consent-properties/snapshot-schedules',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.cp_quality,
          children: [
            {
              label: t.consent_properties.nav.comparison,
              to: '/admin/consent-properties/comparison',
              testId: 'comparison-tab',
            },
            {
              label: t.consent_properties.nav.inconsistencies,
              to: '/admin/consent-properties/inconsistencies',
            },
            {
              label: t.consent_properties.nav.evidence_packs,
              to: '/admin/consent-properties/evidence-packs',
            },
          ],
        },
        { label: t.consent_properties.nav.audit, to: '/admin/consent-properties/audit' },
      ],
    },
    {
      label: (t as any).data_subjects?.nav_title ?? 'Data Subjects',
      to: '/admin/data-subjects',
      icon: 'people',
      requiredPermission: SIDEBAR_PERMISSION_MAP.data_subjects,
      moduleId: SIDEBAR_MODULE_MAP.data_subjects,
      children: [
        {
          label: (t as any).data_subjects?.nav?.profiles ?? 'Profiles',
          to: '/admin/data-subjects',
        },
      ],
    },
    {
      label: t.admin.nav.data_mapping,
      icon: 'data',
      requiredPermission: SIDEBAR_PERMISSION_MAP.data_mapping,
      moduleId: SIDEBAR_MODULE_MAP.data_mapping,
      children: [
        { label: t.data_mapping.nav.flows, to: '/admin/data-mapping/flows' },
        {
          label: t.admin.sidebar_groups.data_mapping_ropa,
          children: [
            { label: t.data_mapping.nav.systems, to: '/admin/data-mapping/systems' },
            { label: t.data_mapping.nav.activities, to: '/admin/data-mapping/activities' },
            {
              label: t.data_mapping.nav.ropa_completeness,
              to: '/admin/data-mapping/ropa-completeness',
            },
            { label: t.data_mapping.nav.categories, to: '/admin/data-mapping/categories' },
            // Sprint 45.4.0 — Data Registry between Categories and Flows.
            { label: t.data_mapping.nav.registry, to: '/admin/data-mapping/registry' },
            { label: t.data_mapping.nav.purposes, to: '/admin/data-mapping/purposes' },
            { label: t.data_mapping.nav.retention, to: '/admin/data-mapping/retention' },
          ],
        },
        {
          label: t.admin.sidebar_groups.data_mapping_classification,
          children: [
            { label: t.data_mapping.nav.review, to: '/admin/data-mapping/review' },
            { label: t.data_mapping.nav.inventory, to: '/admin/data-mapping/inventory' },
            { label: t.data_mapping.nav.exceptions, to: '/admin/data-mapping/exceptions' },
            { label: t.data_mapping.nav.history, to: '/admin/data-mapping/history' },
          ],
        },
        {
          label: t.admin.sidebar_groups.data_mapping_governance,
          children: [
            { label: t.data_mapping.nav.governance, to: '/admin/data-mapping/governance' },
            { label: t.data_mapping.nav.attestation, to: '/admin/data-mapping/attestation' },
            { label: t.data_mapping.nav.evidence, to: '/admin/data-mapping/evidence' },
          ],
        },
        { label: t.data_mapping.nav.exports, to: '/admin/data-mapping/exports' },
      ],
    },
    {
      label: t.admin.nav.deletion_orchestration,
      icon: 'deletion',
      requiredPermission: SIDEBAR_PERMISSION_MAP.deletion_orchestration,
      moduleId: SIDEBAR_MODULE_MAP.deletion_orchestration,
      children: [
        {
          label: t.deletion_orchestration.nav.dashboard,
          to: '/admin/deletion-orchestration/dashboard',
        },
        {
          label: t.admin.sidebar_groups.deletion_execution,
          children: [
            {
              label: t.deletion_orchestration.nav.runs,
              to: '/admin/deletion-orchestration/runs',
            },
            {
              label: t.deletion_orchestration.nav.tasks,
              to: '/admin/deletion-orchestration/tasks',
            },
            {
              label: t.deletion_orchestration.nav.templates,
              to: '/admin/deletion-orchestration/templates',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.deletion_vendors,
          children: [
            {
              label: t.deletion_orchestration.nav.vendor_requests,
              to: '/admin/deletion-orchestration/vendor-requests',
            },
            {
              label: t.deletion_orchestration.nav.vendor_verification,
              to: '/admin/deletion-orchestration/vendor-verification',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.deletion_evidence,
          children: [
            {
              label: t.deletion_orchestration.nav.proof_chain,
              to: '/admin/deletion-orchestration/proof-chain',
            },
            {
              label: t.deletion_orchestration.nav.completeness,
              to: '/admin/deletion-orchestration/completeness',
            },
            {
              label: t.deletion_orchestration.nav.dsr_integration,
              to: '/admin/deletion-orchestration/dsr-integration',
            },
          ],
        },
      ],
    },
    {
      label: t.admin.nav.discovery,
      icon: 'discovery',
      requiredPermission: SIDEBAR_PERMISSION_MAP.discovery,
      moduleId: SIDEBAR_MODULE_MAP.discovery,
      children: [
        { label: t.discovery.nav.dashboard, to: '/admin/discovery/dashboard' },
        {
          label: t.admin.sidebar_groups.discovery_rules,
          children: [
            {
              label: t.discovery.nav.classification_rules,
              to: '/admin/discovery/classification-rules',
            },
            { label: t.discovery.nav.dedup_rules, to: '/admin/discovery/dedup-rules' },
            { label: t.discovery.nav.import_templates, to: '/admin/discovery/import-templates' },
          ],
        },
        {
          label: t.admin.sidebar_groups.discovery_scanning,
          children: [
            { label: t.discovery.nav.connectors, to: '/admin/discovery/connectors' },
            { label: t.discovery.nav.scans, to: '/admin/discovery/scans' },
            { label: t.discovery.nav.discovery_cycles, to: '/admin/discovery/cycles' },
          ],
        },
        {
          label: t.admin.sidebar_groups.discovery_review,
          children: [
            { label: t.discovery.nav.staging_area, to: '/admin/discovery/staging' },
            { label: t.discovery.nav.suggestions, to: '/admin/discovery/suggestions' },
            { label: t.discovery.nav.owner_review, to: '/admin/discovery/owner-review' },
          ],
        },
        {
          label: t.admin.sidebar_groups.discovery_insights,
          children: [
            { label: t.discovery.nav.analytics, to: '/admin/discovery/analytics' },
            {
              label: t.discovery.nav.classification_lineage,
              to: '/admin/discovery/lineage',
            },
          ],
        },
      ],
    },
    {
      label: t.admin.nav.governance,
      icon: 'governance',
      requiredPermission: SIDEBAR_PERMISSION_MAP.governance,
      moduleId: SIDEBAR_MODULE_MAP.governance,
      children: [
        {
          label: t.admin.sidebar_groups.governance_oversight,
          children: [
            { label: t.governance.nav.approvals, to: '/admin/governance/approvals' },
            // Sprint 41.2.0 E6 — Governance Audit Trail
            { label: t.governance.nav.audit_trail, to: '/admin/governance/audit-trail' },
            { label: t.governance.nav.drift, to: '/admin/governance/drift' },
          ],
        },
        {
          label: t.admin.sidebar_groups.governance_reporting,
          children: [
            { label: t.governance.nav.board_report, to: '/admin/governance/board-report' },
            { label: t.governance.nav.scores, to: '/admin/governance/scores' },
          ],
        },
        {
          label: t.admin.sidebar_groups.governance_vendors,
          children: [
            { label: t.governance.nav.vendors, to: '/admin/governance/vendors' },
            { label: t.governance.nav.subprocessors, to: '/admin/governance/subprocessors' },
            { label: t.governance.nav.cross_border, to: '/admin/governance/cross-border' },
            // Sprint 41.5.0 G4 — onboarding/dpa_reviews removed; canonical lifecycles
            // now live on vendor-risk/VendorOnboardingCasePage. Legacy paths redirect
            // via DeprecatedGovernanceRedirect for bookmarked users.
            { label: t.governance.nav.offboarding, to: '/admin/governance/offboarding' },
          ],
        },
        {
          label: t.admin.sidebar_groups.governance_lifecycle,
          children: [
            {
              label: t.governance.nav.periodic_reviews,
              to: '/admin/governance/periodic-reviews',
            },
            { label: t.governance.nav.playbooks, to: '/admin/governance/playbooks' },
            { label: t.governance.nav.evidence, to: '/admin/governance/evidence' },
          ],
        },
      ],
    },
    {
      label: t.admin.nav.incident_ops,
      icon: 'incidents',
      requiredPermission: SIDEBAR_PERMISSION_MAP.incident_ops,
      moduleId: SIDEBAR_MODULE_MAP.incident_ops,
      children: [
        {
          label: t.admin.sidebar_groups.incident_ops_dashboards,
          children: [
            { label: t.incident_ops.nav.sla_dashboard, to: '/admin/incident-ops/sla-dashboard' },
            {
              label: t.incident_ops.nav.coordination_dashboard,
              to: '/admin/incident-ops/coordination-dashboard',
            },
            { label: t.incident_ops.nav.metrics, to: '/admin/incident-ops/metrics' },
          ],
        },
        {
          label: t.admin.sidebar_groups.incident_ops_sla,
          children: [
            {
              label: t.incident_ops.nav.sla_definitions,
              to: '/admin/incident-ops/sla-definitions',
            },
            {
              label: t.incident_ops.nav.regulatory_sla,
              to: '/admin/incident-ops/regulatory-sla',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.incident_ops_playbooks,
          children: [
            { label: t.incident_ops.nav.playbooks, to: '/admin/incident-ops/playbooks' },
            { label: t.incident_ops.nav.playbook_links, to: '/admin/incident-ops/playbook-links' },
          ],
        },
        {
          label: t.admin.sidebar_groups.incident_ops_vendor_regulator,
          children: [
            {
              label: t.incident_ops.nav.vendor_coordination,
              to: '/admin/incident-ops/vendor-coordination',
            },
            {
              label: t.incident_ops.nav.regulator_exports,
              to: '/admin/incident-ops/regulator-exports',
            },
            { label: t.incident_ops.nav.escalation_log, to: '/admin/incident-ops/escalations' },
          ],
        },
        {
          label: t.admin.sidebar_groups.incident_ops_evidence,
          children: [
            { label: t.incident_ops.nav.evidence_chain, to: '/admin/incident-ops/evidence-chain' },
            { label: t.incident_ops.nav.post_review, to: '/admin/incident-ops/post-review' },
          ],
        },
      ],
    },
    {
      label: t.admin.nav.incidents,
      icon: 'incidents',
      requiredPermission: SIDEBAR_PERMISSION_MAP.incidents,
      moduleId: SIDEBAR_MODULE_MAP.incidents,
      children: [
        {
          label: t.admin.sidebar_groups.incidents_workload,
          children: [
            { label: t.incidents.nav.all, to: '/admin/incidents' },
            { label: t.incidents.nav.tasks, to: '/admin/incidents/tasks' },
            { label: t.incidents.nav.remediation, to: '/admin/incidents/remediation' },
          ],
        },
        {
          label: t.admin.sidebar_groups.incidents_regulatory,
          children: [
            {
              label: t.incidents.nav.breach_notifications,
              to: '/admin/incidents/breach-notifications',
            },
            {
              label: t.incidents.nav.authority_requests,
              to: '/admin/incidents/authority-requests',
            },
            {
              label: t.incidents.nav.disclosure_register,
              to: '/admin/incidents/disclosure-register',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.incidents_reporting,
          children: [
            { label: t.incidents.nav.reports, to: '/admin/incidents/reports' },
            { label: t.incidents.nav.supplements, to: '/admin/incidents/supplements' },
          ],
        },
      ],
    },
    {
      label: t.admin.nav.localization,
      icon: 'globe',
      requiredPermission: SIDEBAR_PERMISSION_MAP.localization,
      moduleId: SIDEBAR_MODULE_MAP.localization,
      children: [
        {
          label: t.admin.sidebar_groups.localization_content,
          children: [
            { label: t.localization.nav.translations, to: '/admin/localization/translations' },
            { label: t.localization.nav.language_packs, to: '/admin/localization/language-packs' },
            { label: t.localization.nav.market_packs, to: '/admin/localization/market-packs' },
            { label: 'Help-Content Packs', to: '/admin/localization/help-content-packs' },
            { label: t.localization.nav.mappings, to: '/admin/localization/mappings' },
          ],
        },
        {
          label: t.admin.sidebar_groups.localization_regulatory,
          children: [
            { label: t.localization.nav.frameworks, to: '/admin/localization/frameworks' },
            { label: 'Sector Overlays', to: '/admin/localization/sector-overlays' },
            {
              label:
                (t.admin.nav as unknown as Record<string, string>).regulatory_updates ||
                'Regulatory Updates',
              to: '/admin/localization/regulatory-updates',
            },
            { label: t.localization.nav.submissions, to: '/admin/localization/submissions' },
          ],
        },
        {
          label: t.admin.sidebar_groups.localization_observability,
          children: [
            { label: t.localization.nav.dashboard, to: '/admin/localization/dashboard' },
            { label: t.localization.nav.audit, to: '/admin/localization/audit' },
          ],
        },
      ],
    },
    {
      label: t.admin.nav.monitoring,
      icon: 'monitoring',
      requiredPermission: SIDEBAR_PERMISSION_MAP.monitoring,
      moduleId: SIDEBAR_MODULE_MAP.monitoring,
      children: [
        {
          label: t.admin.sidebar_groups.monitoring_metrics,
          children: [
            { label: t.monitoring.nav.kpi, to: '/admin/monitoring/kpi' },
            {
              label: t.monitoring.nav.authority_readiness,
              to: '/admin/monitoring/authority-readiness',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.monitoring_programs,
          children: [
            { label: t.monitoring.nav.programs, to: '/admin/monitoring/programs' },
            { label: t.monitoring.nav.periods, to: '/admin/monitoring/periods' },
            { label: t.monitoring.nav.framework_link, to: '/admin/monitoring/framework-link' },
          ],
        },
        {
          label: t.admin.sidebar_groups.monitoring_tasks,
          children: [
            { label: t.monitoring.nav.tasks, to: '/admin/monitoring/tasks' },
            { label: t.monitoring.nav.task_templates, to: '/admin/monitoring/task-templates' },
            { label: t.monitoring.nav.raci, to: '/admin/monitoring/raci' },
          ],
        },
        {
          label: t.admin.sidebar_groups.monitoring_controls,
          children: [
            { label: t.monitoring.nav.ric, to: '/admin/monitoring/ric' },
            { label: t.monitoring.nav.change_review, to: '/admin/monitoring/change-review' },
          ],
        },
      ],
    },
    {
      label: t.admin?.nav?.organizations || 'Organizations',
      icon: 'governance',
      requiredPermission: 'org:manage:read',
      moduleId: SIDEBAR_MODULE_MAP.organizations,
      children: [
        {
          label: t.admin.sidebar_groups.org_entities,
          children: [
            { label: t.admin?.nav?.org_list || 'All Organizations', to: '/admin/organizations' },
            {
              label:
                (t.admin.nav as unknown as Record<string, string>).departments || 'Departments',
              to: '/admin/organizations/departments',
            },
            { label: t.admin.nav.org_groups, to: '/admin/org-groups' },
          ],
        },
        {
          label: t.admin.sidebar_groups.org_config,
          children: [
            {
              label: t.admin?.nav?.intake_configs || 'Intake Configs',
              to: '/admin/intake-configs',
            },
            {
              label:
                ((t.admin as any)?.organizations?.retention_policies?.title as string) ||
                'Retention Policies',
              to: '/admin/organizations/retention-policies',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.org_frameworks,
          children: [
            {
              label:
                (t.admin as unknown as Record<string, Record<string, string>>).org_maturity
                  ?.framework_assignments || 'Framework Assignments',
              to: '/admin/organizations/entity-frameworks',
            },
            {
              label:
                (t.admin as unknown as Record<string, Record<string, string>>).org_maturity
                  ?.compliance_rollup || 'Compliance Rollup',
              to: '/admin/organizations/compliance-rollup',
            },
            {
              label:
                (t.admin.nav as unknown as Record<string, string>).consolidation || 'Consolidation',
              to: '/admin/consolidation',
            },
          ],
        },
      ],
    },
    {
      label: t.admin.nav.partner_workspace,
      icon: 'governance',
      requiredPermission: SIDEBAR_PERMISSION_MAP.partner_workspace,
      moduleId: SIDEBAR_MODULE_MAP.partner_workspace,
      children: [
        {
          label: t.admin.sidebar_groups.partner_overview,
          children: [
            {
              label: t.partner_marketplace.nav.dashboard,
              to: '/admin/partner-marketplace/dashboard',
            },
            { label: t.partner_marketplace.nav.reports, to: '/admin/partner-marketplace/reports' },
          ],
        },
        {
          label: t.admin.sidebar_groups.partner_partners,
          children: [
            {
              label: t.partner_marketplace.nav.partner_orgs,
              to: '/admin/partner-marketplace/partner-orgs',
            },
            {
              label: t.partner_marketplace.nav.brand_profiles,
              to: '/admin/partner-marketplace/brand-profiles',
            },
            {
              label: t.partner_marketplace.nav.service_tiers,
              to: '/admin/partner-marketplace/service-tiers',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.partner_delivery,
          children: [
            {
              label: t.partner_marketplace.nav.deliverables,
              to: '/admin/partner-marketplace/deliverables',
            },
            {
              label: t.partner_marketplace.nav.deliverable_templates,
              to: '/admin/partner-marketplace/deliverable-templates',
            },
            {
              label: t.partner_marketplace.nav.workflows,
              to: '/admin/partner-marketplace/workflows',
            },
            {
              label: t.partner_marketplace.nav.workflow_templates,
              to: '/admin/partner-marketplace/workflow-templates',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.partner_support,
          children: [
            {
              label: t.partner_marketplace.nav.support_tickets,
              to: '/admin/partner-marketplace/support-tickets',
            },
            {
              label: t.partner_marketplace.nav.escalation,
              to: '/admin/partner-marketplace/escalation',
            },
            { label: t.partner_marketplace.nav.audit, to: '/admin/partner-marketplace/audit' },
          ],
        },
      ],
    },
    {
      // Sprint 47.5.2 — Forseti AI Legal Advisor.
      // Sprint 47.9.9 — moved to immediately before Onboarding so the
      // platform-intelligence layer sits adjacent to the meta tooling
      // group (Onboarding / Platform / Help Center) rather than mid-
      // workflow between Discovery and Governance. Mirrors the manual
      // sidebar tree's bottom_standalone ordering at
      // `agent-os/scripts/help/sidebar-menu-tree.json`.
      label: t.admin.nav.forseti ?? 'Forseti',
      icon: 'audit',
      requiredPermission: SIDEBAR_PERMISSION_MAP.forseti,
      moduleId: SIDEBAR_MODULE_MAP.forseti,
      children: [
        // Sprint 47.5.4a — Chats sub-item (overview at /admin/forseti).
        {
          label: t.forseti?.chat?.sidebarLabel ?? 'Chats',
          to: '/admin/forseti',
        },
        {
          label: t.forseti?.projects?.title ?? 'Projects',
          to: '/admin/forseti/projects',
        },
        {
          label: t.forseti?.workflows?.pageTitle ?? 'Workflows',
          to: '/admin/forseti/workflows',
          requiredPermission: Permission.AI_WORKFLOWS_READ,
        },
        {
          label: t.forseti?.tabularReviews?.pageTitle ?? 'Tabular Reviews',
          to: '/admin/forseti/tabular-reviews',
          requiredPermission: Permission.AI_TABULAR_REVIEWS_READ,
        },
        {
          // Sprint 47.8.0a — surface the previously orphan
          // `/admin/forseti/document-review` route via a new index page.
          label: t.forseti?.documentReviewIndex?.sidebarLabel ?? 'Document Review',
          to: '/admin/forseti/document-review',
          requiredPermission: Permission.AI_ADVISOR_CHAT,
        },
        {
          label: t.forseti?.auditLineage?.title ?? 'Audit Lineage',
          to: '/admin/forseti/audit-lineage',
          requiredPermission: Permission.AI_ADVISOR_CHAT,
        },
        {
          // Sprint 47.7.3 — cross-framework conflict detection.
          label: t.forseti?.crossFrameworkConflicts?.pageTitle ?? 'Cross-framework conflicts',
          to: '/admin/forseti/cross-framework-conflicts',
          requiredPermission: Permission.AI_ADVISOR_CHAT,
        },
        {
          // Sprint 47.5.8.6 — AI evidence review queue. R-34 hides if the caller
          // can't even chat with Forseti; the page itself is further gated by
          // the literal `evidence:review` permission inside the backend route.
          label: t.forseti?.evidenceDraft?.pageTitle ?? 'AI evidence drafts',
          to: '/admin/forseti/draft-evidence',
          requiredPermission: Permission.AI_ADVISOR_CHAT,
        },
        {
          label: t.forseti?.usage?.title ?? 'Usage',
          to: '/admin/forseti/usage',
        },
        {
          // Sprint 47.9.7 — MCP connector admin (Phase 1 read-only).
          // Permission-gated by `ai_provider:mcp:list`; non-admins
          // never see the entry per R-34.
          label: t.forseti?.mcp?.pageTitle ?? 'MCP Keys',
          to: '/admin/forseti/mcp-keys',
          requiredPermission: Permission.AI_PROVIDER_MCP_LIST,
        },
        {
          // Sprint 48.3.0 — MCP Phase 2 pending writes admin queue.
          label: (t as Record<string, any>).mcp_pending?.title ?? 'MCP Pending',
          to: '/admin/forseti/mcp-pending',
          requiredPermission: Permission.AI_PROVIDER_MCP_LIST,
        },
        {
          // Sprint 48.0.0c — Forseti memory browse / curate. Visible to
          // anyone with `:read`; per R-34 hides for tenants without it.
          label: t.forseti?.memory?.pageTitle ?? 'Memory',
          to: '/admin/forseti/memory',
          requiredPermission: Permission.AI_ADVISOR_MEMORY_READ,
        },
        {
          // Sprint 48.2.8 — Compliance rule auto-mapping (regulatory pack
          // → control library).
          label:
            (t as Record<string, any>).compliance_rules?.rule_mapping?.title ?? 'Rule auto-mapping',
          to: '/admin/forseti/rule-mapping',
          requiredPermission: Permission.AI_ADVISOR_CHAT,
        },
        {
          // Sprint 48.2.9 — Forseti audit walkthrough generator (forseti-auditor persona).
          label: (t as Record<string, any>).audit_walkthrough?.title ?? 'Audit walkthrough',
          to: '/admin/forseti/audit-walkthrough',
          requiredPermission: Permission.AI_ADVISOR_CHAT,
        },
        {
          // Sprint 48.2.9 — Privilege creep detection (forseti-auditor persona).
          label: (t as Record<string, any>).privilege_creep?.title ?? 'Privilege creep',
          to: '/admin/forseti/privilege-creep',
          requiredPermission: Permission.AI_ADVISOR_CHAT,
        },
      ],
    },
    {
      label: t.admin.nav.onboarding,
      icon: 'onboarding',
      moduleId: SIDEBAR_MODULE_MAP.onboarding,
      requiredPermission: SIDEBAR_PERMISSION_MAP.onboarding,
      children: [
        { label: t.onboarding.nav.wizard, to: '/admin/onboarding/setup' },
        { label: t.onboarding.nav.tours, to: '/admin/onboarding/tours' },
        { label: t.onboarding.nav.sample_data, to: '/admin/onboarding/sample-data' },
        {
          label:
            (t.admin as unknown as Record<string, Record<string, string>>).onboarding_maturity
              ?.quick_starts || 'Quick Starts',
          to: '/admin/onboarding/quick-starts',
        },
        {
          label:
            (t.admin as unknown as Record<string, Record<string, string>>).onboarding_maturity
              ?.progress || 'Onboarding Progress',
          to: '/admin/onboarding/progress',
        },
        {
          label:
            (t.admin as unknown as Record<string, Record<string, string>>).onboarding_maturity
              ?.personas || 'Persona Paths',
          to: '/admin/onboarding/personas',
        },
      ],
    },
    {
      label: t.admin.nav.platform,
      icon: 'platform',
      requiredPermission: SIDEBAR_PERMISSION_MAP.platform,
      children: [
        {
          label: t.admin.sidebar_groups.platform_identity,
          children: [
            { label: t.platform.nav.users, to: '/admin/users' },
            { label: t.platform.nav.roles, to: '/admin/roles' },
            { label: t.platform.security.role_analytics, to: '/admin/access-reviews/analytics' },
            { label: t.platform.security.service_accounts, to: '/admin/security/service-accounts' },
          ],
        },
        {
          label: t.admin.sidebar_groups.platform_access_control,
          children: [
            { label: t.platform.security.api_keys, to: '/admin/security/api-keys' },
            { label: t.platform.security.delegations, to: '/admin/security/delegations' },
            { label: t.platform.security.sod_rules, to: '/admin/sod-rules' },
            { label: t.platform.security.sod_violations, to: '/admin/sod-rules/violations' },
          ],
        },
        {
          label: t.admin.sidebar_groups.platform_auth_session,
          children: [
            { label: t.platform.security.mfa, to: '/admin/security/mfa' },
            ...(!isProduction
              ? [{ label: t.platform.security.sso, to: '/admin/security/sso' }]
              : []),
            { label: t.platform.security.active_sessions, to: '/admin/security/active-sessions' },
            { label: t.platform.security.session_settings, to: '/admin/security/session-settings' },
            { label: t.platform.security.ip_allowlist, to: '/admin/security/ip-allowlist' },
          ],
        },
        {
          label: t.admin.sidebar_groups.platform_access_reviews,
          children: [
            { label: t.platform.security.access_reviews, to: '/admin/access-reviews' },
            { label: t.platform.security.my_reviews, to: '/admin/access-reviews/my-reviews' },
            {
              label: t.platform.security.attestation_schedules,
              to: '/admin/attestation-schedules',
            },
            {
              label: (t as any).assessment_triggers?.title ?? 'Assessment Triggers',
              to: '/admin/settings/assessment-triggers',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.platform_files,
          children: [
            // Sprint 41.4.0 — Platform → Files (Configuration / Browser / Dashboard)
            {
              label:
                (t.platform as unknown as Record<string, Record<string, string>>)?.files_nav
                  ?.configuration ?? 'Files Configuration',
              to: '/admin/platform/files/config',
              requiredPermission: 'platform:files:config:read',
            },
            {
              label:
                (t.platform as unknown as Record<string, Record<string, string>>)?.files_nav
                  ?.browser ?? 'File Browser',
              to: '/admin/platform/files/browser',
              requiredPermission: 'platform:files:browse',
            },
            {
              label:
                (t.platform as unknown as Record<string, Record<string, string>>)?.files_nav
                  ?.dashboard ?? 'Files Dashboard',
              to: '/admin/platform/files/dashboard',
              requiredPermission: 'platform:files:metrics:read',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.platform_notifications,
          children: [
            {
              label:
                (t.notifications as unknown as Record<string, Record<string, string>>)?.admin_queue
                  ?.title ?? 'Notification Admin Queue',
              to: '/admin/platform/notification-admin-queue',
              requiredPermission: 'platform:notifications:admin',
            },
            {
              label: t.notifications?.preferences?.title ?? 'Notification Preferences',
              to: '/admin/platform/notification-preferences',
            },
            // Sprint 58.5.0 post-merge QA fix — PDPL Evidence Pack email-providers
            // moved from a top-level section (E1b placement) into Platform → Notifications.
            // The pack flow itself is deep-linked from DPIA dossiers; only the
            // provider configuration belongs in the platform-wide config namespace.
            {
              label: t.pdplEvidencePacks.nav.emailProviders,
              to: '/admin/pdpl-evidence-packs/email-providers',
            },
          ],
        },
        {
          // Sprint 47.4.0 — Platform → Audit & Proof (foundation shells).
          // Editable surfaces ship in 47.4.4 (usage), 47.4.5 (status),
          // 47.4.6 (integrity center), 47.4.7 (settings).
          label:
            (t.platform as unknown as Record<string, Record<string, string>>)
              ?.immutable_audit_trails?.nav_group_label ?? 'Audit & Proof',
          children: [
            {
              label:
                (t.platform as unknown as Record<string, Record<string, string>>)
                  ?.immutable_audit_trails?.nav_settings ?? 'Settings',
              to: '/admin/platform/immutable-audit-trails/settings',
              requiredPermission: 'platform:immutable_audit_trails:read',
            },
            {
              label:
                (t.platform as unknown as Record<string, Record<string, string>>)
                  ?.immutable_audit_trails?.nav_status ?? 'Proof Status',
              to: '/admin/platform/immutable-audit-trails/status',
              requiredPermission: 'platform:immutable_audit_trails:read',
            },
            {
              label:
                (t.platform as unknown as Record<string, Record<string, string>>)
                  ?.immutable_audit_trails?.nav_integrity_center ?? 'Integrity Center',
              to: '/admin/platform/immutable-audit-trails/integrity-center',
              requiredPermission: 'platform:immutable_audit_trails:read',
            },
            {
              label:
                (t.platform as unknown as Record<string, Record<string, string>>)
                  ?.immutable_audit_trails?.nav_usage ?? 'Usage & Credits',
              to: '/admin/platform/immutable-audit-trails/usage',
              requiredPermission: 'platform:immutable_audit_trails:read',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.platform_module_config,
          children: [
            { label: t.platform.module_config.nav_label, to: '/admin/platform/modules' },
            { label: t.platform.module_config.settings, to: '/admin/platform/settings' },
            { label: t.platform.module_config.domains, to: '/admin/platform/domains' },
            { label: t.platform.module_config.webhooks, to: '/admin/platform/webhooks' },
          ],
        },
        {
          label: t.admin.sidebar_groups.platform_ops,
          children: [
            { label: t.platform.module_config.email_log, to: '/admin/platform/email' },
            {
              label: t.platform.module_config.export_schedules,
              to: '/admin/platform/export-schedules',
            },
          ],
        },
        // Standalone leaves (kept top-level within the Platform module):
        // Break Glass is a deliberate red-button surface; Profile + License
        // are user-level concerns that benefit from quick access.
        { label: t.platform.security.break_glass, to: '/admin/security/break-glass' },
        { label: t.platform.nav.profile, to: '/admin/profile' },
        {
          label: t.license.title,
          to: '/admin/settings/license',
          requiredPermission: 'license:read',
        },
      ],
    },
    {
      label: t.admin.nav.privacy_scanner,
      icon: 'discovery',
      requiredPermission: SIDEBAR_PERMISSION_MAP.privacy_scanner,
      moduleId: SIDEBAR_MODULE_MAP.privacy_scanner,
      children: [
        { label: t.admin.nav.privacy_monitoring, to: '/admin/privacy-scanner/monitoring' },
        { label: t.admin.nav.real_time_scanner, to: '/admin/privacy-scanner/real-time' },
      ],
    },
    {
      label: t.admin.nav.program_governance,
      icon: 'assessments',
      requiredPermission: SIDEBAR_PERMISSION_MAP.program_governance,
      moduleId: SIDEBAR_MODULE_MAP.program_governance,
      children: [
        {
          label: t.admin.sidebar_groups.pg_health,
          children: [
            {
              label: t.program_governance.nav.program_health,
              to: '/admin/program-governance/health',
            },
            {
              label: t.program_governance.nav.trend_dashboard,
              to: '/admin/program-governance/trends',
            },
            {
              label: t.program_governance.nav.executive_summaries,
              to: '/admin/program-governance/summaries',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.pg_maturity,
          children: [
            {
              label: t.program_governance.nav.maturity_assessments,
              to: '/admin/program-governance/assessments',
            },
            {
              label: t.program_governance.nav.maturity_models,
              to: '/admin/program-governance/models',
            },
            {
              label: t.program_governance.nav.governance_model,
              to: '/admin/program-governance/governance-model',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.pg_audit,
          children: [
            {
              label: t.program_governance.nav.audit_readiness,
              to: '/admin/program-governance/readiness',
            },
            {
              label: t.program_governance.nav.evidence_gaps,
              to: '/admin/program-governance/evidence-gaps',
            },
            {
              label: t.program_governance.nav.remediation_plans,
              to: '/admin/program-governance/remediation-plans',
            },
            {
              label: t.program_governance.nav.reporting_periods,
              to: '/admin/program-governance/reporting-periods',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.pg_obligations,
          children: [
            {
              label: t.program_governance.nav.obligation_mappings,
              to: '/admin/program-governance/obligation-mappings',
            },
            {
              label: t.program_governance.nav.change_log,
              to: '/admin/program-governance/change-log',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.pg_documents,
          children: [
            {
              label: t.program_governance.nav.governance_documents,
              to: '/admin/program-governance/governance-documents',
            },
            {
              label: t.program_governance.nav.document_drafting,
              to: '/admin/program-governance/document-drafting',
            },
            {
              label: t.program_governance.nav.document_issuance,
              to: '/admin/program-governance/document-issuance',
            },
            {
              label: t.program_governance.nav.acknowledgment_tracking,
              to: '/admin/program-governance/acknowledgment-tracking',
            },
            {
              label: t.program_governance.nav.review_cadence,
              to: '/admin/program-governance/review-cadence',
            },
          ],
        },
      ],
    },
    {
      label: t.admin.nav.rights_requests,
      icon: 'rights',
      requiredPermission: SIDEBAR_PERMISSION_MAP.rights_requests,
      moduleId: SIDEBAR_MODULE_MAP.rights_requests,
      children: [
        { label: t.admin.nav.rights_requests, to: '/admin/rights-requests' },
        {
          label: t.rights_requests?.import?.nav_title ?? 'Bulk Import',
          to: '/admin/rights-requests/import',
        },
        // Sprint 48.1.1 — DSR Batch Process (Forseti AI workflow).
        {
          label: t.rights_requests?.batch?.nav_title ?? 'Batch Process',
          to: '/admin/rights-requests/batch-autocomplete',
          requiredPermission: Permission.AI_ADVISOR_CHAT,
        },
        /* Sprint 43.5.1 consolidation — the Phase 1 Sprint 1.1.0 TemplatesPage
         * at `/admin/templates` and the Sprint 43.2.0 ResponseTemplatesAdminPage
         * at `/admin/rights-requests/response-templates` both appeared side-by-side
         * under Rights Requests in the sidebar, both rendering as "Response
         * Templates". The newer DsrResponseTemplate CRUD (outcome types,
         * official_form_id, is_active soft-delete) is canonical — keep only that
         * entry. The old `/admin/templates` route is left intact for backwards
         * compatibility with any bookmarked URLs, but no longer navigable
         * from the sidebar.
         */
        {
          label:
            ((t.admin as any)?.rights_requests?.response_templates?.title as string) ||
            'Response Templates',
          to: '/admin/rights-requests/response-templates',
        },
        ...(!isProduction
          ? [
              {
                label: t.admin.nav.cmp_dsar_sync,
                to: '/admin/rights-requests/settings/cmp-integration',
              },
            ]
          : []),
      ],
    },
    {
      label: t.admin.nav.task_management,
      icon: 'monitoring',
      requiredPermission: SIDEBAR_PERMISSION_MAP.task_management,
      moduleId: SIDEBAR_MODULE_MAP.task_management,
      children: [
        { label: t.task_management.nav.all_tasks, to: '/admin/tasks' },
        { label: t.task_management.nav.board, to: '/admin/tasks/board' },
        { label: t.task_management.nav.calendar, to: '/admin/tasks/calendar' },
        { label: t.task_management.nav.my_tasks, to: '/admin/tasks/my' },
        { label: t.task_management.nav.overdue, to: '/admin/tasks/overdue' },
        { label: t.task_management.nav.workflows, to: '/admin/tasks/workflows' },
      ],
    },
    {
      label: t.admin.nav.vendor_risk,
      icon: 'governance',
      requiredPermission: SIDEBAR_PERMISSION_MAP.vendor_risk,
      moduleId: SIDEBAR_MODULE_MAP.vendor_risk,
      children: [
        {
          label: t.admin.sidebar_groups.vr_overview,
          children: [
            { label: t.vendor_risk.nav.dashboard, to: '/admin/vendor-risk/dashboard' },
            { label: t.vendor_risk.nav.portfolio, to: '/admin/vendor-risk/portfolio' },
          ],
        },
        {
          label: t.admin.sidebar_groups.vr_onboarding,
          children: [
            {
              label: t.vendor_risk.nav.onboarding_cases,
              to: '/admin/vendor-risk/onboarding-cases',
            },
            { label: t.vendor_risk.nav.gates, to: '/admin/vendor-risk/gates' },
            // Sprint 47.5.8.1 — Contracts is its own first-class module; sidebar entry stays
            // nested under Vendor & Third-Party Risk for affinity but the route is flat.
            { label: t.vendor_risk.nav.contracts, to: '/admin/contracts' },
          ],
        },
        {
          label: t.admin.sidebar_groups.vr_assessment,
          children: [
            { label: t.vendor_risk.nav.questionnaires, to: '/admin/vendor-risk/questionnaires' },
            { label: t.vendor_risk.nav.scoring, to: '/admin/vendor-risk/scoring' },
            { label: t.vendor_risk.nav.reviews, to: '/admin/vendor-risk/reviews' },
            { label: t.vendor_risk.nav.reassessments, to: '/admin/vendor-risk/reassessments' },
          ],
        },
        {
          label: t.admin.sidebar_groups.vr_oversight,
          children: [
            { label: t.vendor_risk.nav.signals, to: '/admin/vendor-risk/signals' },
            { label: t.vendor_risk.nav.acceptances, to: '/admin/vendor-risk/acceptances' },
            { label: t.vendor_risk.nav.review_routing, to: '/admin/vendor-risk/review-routing' },
            {
              label: t.vendor_risk.nav.sub_processor_register,
              to: '/admin/vendor-risk/sub-processor-register',
            },
          ],
        },
      ],
    },
    // Help Center always last
    { label: t.admin.nav.help_center, to: '/admin/help', icon: 'help' },
  ];

  // Filter sidebar sections by permission AND module enabled state
  const visibleMenuItems = useMemo(
    () =>
      menuItems.filter((item) => {
        // Check RBAC permission
        if (item.requiredPermission && !hasPermission(item.requiredPermission)) return false;
        // Check module enabled state
        if (item.moduleId && !isModuleEnabled(item.moduleId)) return false;
        return true;
      }),
    [menuItems, hasPermission, isModuleEnabled],
  );

  // Auto-expand sections that contain the active route
  const getInitialExpanded = useCallback(() => {
    const expanded: Record<number, boolean> = {};
    visibleMenuItems.forEach((item, idx) => {
      if (item.children) {
        const isActive = flattenLeaves(item.children).some((leaf) =>
          location.pathname.startsWith(leaf.to),
        );
        if (isActive) expanded[idx] = true;
      }
    });
    return expanded;
  }, [location.pathname]);

  const [expanded, setExpanded] = useState<Record<number, boolean>>(getInitialExpanded);

  const toggleSection = (idx: number) => {
    setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  // Auto-expand sub-groups (3rd tier) that contain the active route.
  const getInitialSubExpanded = useCallback(() => {
    const sub: Record<string, boolean> = {};
    visibleMenuItems.forEach((item, idx) => {
      if (!item.children) return;
      item.children.forEach((child, cIdx) => {
        if (isSubGroup(child)) {
          const isActive = child.children.some((leaf) => location.pathname.startsWith(leaf.to));
          if (isActive) sub[`${idx}:${cIdx}`] = true;
        }
      });
    });
    return sub;
  }, [location.pathname]);

  const [subExpanded, setSubExpanded] = useState<Record<string, boolean>>(getInitialSubExpanded);

  const toggleSubGroup = (key: string) => {
    setSubExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Sprint 25.3.4: Category fold/unfold state
  const [categoryExpanded, setCategoryExpanded] =
    useState<Record<string, boolean>>(loadCategoryState);

  // Auto-expand category containing active route
  const activeCategoryKey = useMemo(() => {
    for (const cat of SIDEBAR_CATEGORIES) {
      if (cat.modules.some((m) => location.pathname.startsWith(m))) return cat.key;
    }
    return null;
  }, [location.pathname]);

  // Ensure active category is expanded on navigation
  if (activeCategoryKey && !categoryExpanded[activeCategoryKey]) {
    setCategoryExpanded((prev) => ({ ...prev, [activeCategoryKey]: true }));
  }

  const toggleCategory = useCallback((key: string) => {
    setCategoryExpanded((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      saveCategoryState(next);
      return next;
    });
  }, []);

  // Group visible items by category
  const { standalone, categorized } = useMemo(() => {
    const standaloneItems: { item: MenuItem; idx: number }[] = [];
    const catMap: Record<string, { item: MenuItem; idx: number }[]> = {};
    for (const cat of SIDEBAR_CATEGORIES) catMap[cat.key] = [];

    visibleMenuItems.forEach((item, idx) => {
      const catKey = getCategoryForItem(item);
      if (catKey && catMap[catKey]) {
        catMap[catKey].push({ item, idx });
      } else {
        standaloneItems.push({ item, idx });
      }
    });
    return { standalone: standaloneItems, categorized: catMap };
  }, [visibleMenuItems]);

  const categoryLabels: Record<string, string> = {
    data_privacy_rights:
      (t.admin as unknown as Record<string, string>)?.sidebar_category_data_privacy_rights ??
      'Data Privacy & Rights',
    vendor_third_party:
      (t.admin as unknown as Record<string, string>)?.sidebar_category_vendor_third_party ??
      'Vendor & Third-Party Management',
    risk_assessment:
      (t.admin as unknown as Record<string, string>)?.sidebar_category_risk_assessment ??
      'Risk & Assessment',
    operations_automation:
      (t.admin as unknown as Record<string, string>)?.sidebar_category_operations_automation ??
      'Operations & Automation',
    task_workflow:
      (t.admin as unknown as Record<string, string>)?.sidebar_category_task_workflow ??
      'Task & Workflow Management',
  };

  // ── Sidebar render helpers (shared by categorized + standalone paths) ───
  const Chevron = () => (
    <svg className="nav-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M6 4l4 4-4 4"
        stroke="#1ab394"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const visibleLeaves = (leaves: MenuLeaf[]) =>
    leaves.filter((l) => !l.requiredPermission || hasPermission(l.requiredPermission));

  const needsEndFor = (leaf: MenuLeaf, allLeaves: MenuLeaf[]) =>
    allLeaves.some((sib) => sib.to !== leaf.to && sib.to.startsWith(leaf.to + '/'));

  const renderLeaf = (leaf: MenuLeaf, allLeaves: MenuLeaf[]) => (
    <li key={leaf.to}>
      <NavLink to={leaf.to} end={needsEndFor(leaf, allLeaves)} data-testid={leaf.testId}>
        {leaf.label}
      </NavLink>
    </li>
  );

  const renderModuleContents = (item: MenuItem, idx: number) => {
    if (!item.children) return null;
    const allLeaves = flattenLeaves(item.children);
    return (
      <ul className={`nav-sub${expanded[idx] ? ' nav-sub-open' : ''}`}>
        {item.children.map((child, cIdx) => {
          if (isSubGroup(child)) {
            const subLeaves = visibleLeaves(child.children);
            if (subLeaves.length === 0) return null;
            const key = `${idx}:${cIdx}`;
            const open = !!subExpanded[key];
            return (
              <li key={`sg-${cIdx}`} className="nav-subgroup">
                <button
                  className={`nav-subgroup-toggle${open ? ' open' : ''}`}
                  onClick={() => toggleSubGroup(key)}
                  aria-expanded={open}
                >
                  <span className="nav-subgroup-label">{child.label}</span>
                  <Chevron />
                </button>
                <ul className={`nav-subsub${open ? ' nav-subsub-open' : ''}`}>
                  {subLeaves.map((leaf) => renderLeaf(leaf, allLeaves))}
                </ul>
              </li>
            );
          }
          if (child.requiredPermission && !hasPermission(child.requiredPermission)) return null;
          return renderLeaf(child, allLeaves);
        })}
      </ul>
    );
  };

  const renderGroupedModule = (item: MenuItem, idx: number) => (
    <li key={idx} className="nav-group">
      <button
        className={`nav-group-toggle${expanded[idx] ? ' open' : ''}`}
        onClick={() => toggleSection(idx)}
        aria-expanded={!!expanded[idx]}
      >
        <span className="nav-group-label">
          {item.icon && <MenuIcon name={item.icon} />}
          {item.label}
        </span>
        <Chevron />
      </button>
      {renderModuleContents(item, idx)}
    </li>
  );

  return (
    <UIAssistProvider>
      <TourProvider>
        <div className="admin-layout" data-testid="page-ready">
          <SkipToContent />
          {/* Sprint 25.3.4.1: Mobile hamburger button */}
          <button
            ref={hamburgerRef}
            className="mobile-hamburger"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileMenuOpen}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              {mobileMenuOpen ? (
                <path
                  d="M6 6l12 12M6 18L18 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              ) : (
                <path
                  d="M4 6h16M4 12h16M4 18h16"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              )}
            </svg>
          </button>
          {/* Sprint 25.3.4.1: Mobile overlay backdrop */}
          {mobileMenuOpen && (
            <div
              className="mobile-sidebar-backdrop"
              onClick={() => setMobileMenuOpen(false)}
              aria-hidden="true"
            />
          )}
          <nav
            ref={sidebarRef}
            className={`admin-sidebar${mobileMenuOpen ? ' mobile-open' : ''}`}
            aria-label={t.common.sidebar_navigation}
          >
            <div className="admin-logo">
              <img
                src="/logo_compliance.png"
                alt="AesirX ComplianceOne"
                style={{ height: 80, width: 'auto' }}
              />
            </div>
            <ul className="admin-nav">
              {/* Dashboard (standalone at top) */}
              {standalone
                .filter(({ item }) => item.to === '/admin')
                .map(({ item, idx }) => (
                  <li key={idx}>
                    <NavLink to={item.to!} end>
                      {item.icon && <MenuIcon name={item.icon} />}
                      {item.label}
                    </NavLink>
                  </li>
                ))}

              {/* Sprint 25.3.4: Categorized module groups */}
              {SIDEBAR_CATEGORIES.map((cat) => {
                const items = categorized[cat.key];
                if (!items || items.length === 0) return null;
                const isOpen = categoryExpanded[cat.key] !== false;
                const headerId = `category-${cat.key}-header`;
                const groupId = `category-${cat.key}-items`;
                return (
                  <li key={cat.key} className="nav-category">
                    <button
                      id={headerId}
                      className={`nav-category-toggle${isOpen ? ' open' : ''}`}
                      onClick={() => toggleCategory(cat.key)}
                      aria-expanded={isOpen}
                      aria-controls={groupId}
                      aria-label={`${categoryLabels[cat.key]}, ${(items.length === 1 ? t.common.items_count_one : t.common.items_count).replace('{count}', String(items.length))}`}
                    >
                      <span className="nav-category-label">{categoryLabels[cat.key]}</span>
                      <span style={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1 }}>
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>
                    {isOpen && (
                      <ul id={groupId} role="group" aria-labelledby={headerId}>
                        {items.map(({ item, idx }) =>
                          item.children ? (
                            renderGroupedModule(item, idx)
                          ) : (
                            <li key={idx}>
                              <NavLink to={item.to!}>
                                {item.icon && <MenuIcon name={item.icon} />}
                                {item.label}
                              </NavLink>
                            </li>
                          ),
                        )}
                      </ul>
                    )}
                  </li>
                );
              })}

              {/* Platform + Help Center (standalone at bottom) */}
              {standalone
                .filter(({ item }) => item.to !== '/admin')
                .map(({ item, idx }) =>
                  item.children ? (
                    renderGroupedModule(item, idx)
                  ) : (
                    <li key={idx}>
                      <NavLink to={item.to!}>
                        {item.icon && <MenuIcon name={item.icon} />}
                        {item.label}
                      </NavLink>
                    </li>
                  ),
                )}
            </ul>
            <div className="admin-locale-switcher">
              <select
                value={locale}
                onChange={(e) => setLocale(e.target.value)}
                aria-label={t.common.select_language ?? 'Select language'}
              >
                {languages.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            {user && (
              <div className="admin-user-info">
                <span>{user.role_name}</span>
                <button onClick={logout} className="btn-logout" aria-label={t.common.logout}>
                  {t.common.logout}
                </button>
              </div>
            )}
          </nav>
          <main
            id="main-content"
            tabIndex={-1}
            className="admin-content"
            data-module={activeModule}
          >
            {user && (
              <div className="admin-topbar">
                <NotificationBell t={t} />
              </div>
            )}
            <Outlet />
          </main>
        </div>
        <GlobalTourRunner />
      </TourProvider>
    </UIAssistProvider>
  );
}

/** Renders TourRunner at layout level so it survives route changes. */
function GlobalTourRunner() {
  const { activeScript, activeTourMode, startStepIndex, launchId, clearTour } = useTourContext();

  const handleComplete = useCallback(
    async (module: string) => {
      try {
        await apiClient.completeTour(module);
      } catch (_) {
        /* ignore */
      }
      clearTour();
    },
    [clearTour],
  );

  const handleExit = useCallback(async () => {
    if (activeScript) {
      try {
        await apiClient.dismissTour(activeScript.module);
      } catch (_) {
        /* ignore */
      }
    }
    clearTour();
  }, [activeScript, clearTour]);

  if (!activeScript) return null;

  return (
    <TourRunner
      key={launchId}
      script={activeScript}
      mode={activeTourMode}
      startStepIndex={startStepIndex}
      onComplete={handleComplete}
      onExit={handleExit}
    />
  );
}
